import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DEMO_FEED_POSTS } from '../portals/demo/demoFeedData';
import { appLanguage, appLocale } from '../i18n/locale';

// ==================== TYPES ====================
interface Comment {
  id: string;
  userId: string;
  userName: string;
  text: string;
  createdAt: string | { _seconds: number };
}

interface NewsfeedPost {
  id: string;
  type: string;
  author: string;
  title: string;
  titleDe: string;
  content: string;
  contentDe: string;
  imageUrls: string[];
  videoUrl?: string;
  actionLabel: string | null;
  actionUrl: string | null;
  actionLabel2: string | null;
  actionUrl2: string | null;
  commentsEnabled: boolean;
  pinned: boolean;
  likes: string[];
  comments: Comment[];
  viewCount: number;
  createdAt: string | { _seconds: number };
}

interface Story {
  id: string;
  title: string;
  titleDe?: string;
  imageUrl: string;
  description?: string;
  descriptionDe?: string;
  linkLabel?: string;
  linkUrl?: string;
  category: 'listing' | 'unit' | 'offer' | 'update' | 'promo';
  project?: string;
  price?: string;
  bedrooms?: string;
  location?: string;
  createdAt: string | { _seconds: number };
  expiresAt?: string | { _seconds: number };
  viewCount?: number;
}

interface NewsfeedViewProps {
  investorId: string;
  investorName: string;
  isDE: boolean;
  apiUrl: string;
  onNavigate?: (path: string) => void;
  /**
   * Code-gated showcase (portals/demo/DemoShell): render sample posts instead
   * of fetching. Without this the feed shows "No posts yet" in demo mode —
   * API traffic is blocked — which reads as a broken feature rather than a
   * deliberate sample.
   */
  demoMode?: boolean;
}

// ==================== HELPERS ====================
const getTimeAgo = (ts: string | { _seconds: number }): string => {
  const date = typeof ts === 'object' && '_seconds' in ts
    ? new Date(ts._seconds * 1000)
    : new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const de = appLanguage() === 'de';
  if (diffMins < 1) return de ? 'Gerade eben' : 'Just now';
  if (diffMins < 60) return de ? `vor ${diffMins} Min.` : `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return de ? `vor ${diffHours} Std.` : `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return de ? `vor ${diffDays} ${diffDays === 1 ? 'Tag' : 'Tagen'}` : `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) return de ? `vor ${diffWeeks} ${diffWeeks === 1 ? 'Woche' : 'Wochen'}` : `${diffWeeks}w ago`;
  return date.toLocaleDateString(appLocale());
};

const getInitials = (name: string): string => {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

// Fix Google Drive URLs to use thumbnail endpoint (uc?export=view gets blocked)
const fixDriveImageUrl = (url: string): string => {
  if (!url) return url;
  // Match: https://drive.google.com/uc?export=view&id=FILE_ID
  const ucMatch = url.match(/drive\.google\.com\/uc\?export=view&id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) {
    return `https://lh3.googleusercontent.com/d/${ucMatch[1]}=s1000`;
  }
  // Match: https://drive.google.com/file/d/FILE_ID/view
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) {
    return `https://lh3.googleusercontent.com/d/${fileMatch[1]}=s1000`;
  }
  return url;
};

// Check if a URL is a video (by extension or known video hosts)
const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return /\.(mp4|webm|ogg|mov)(\?|$)/.test(lower) ||
    lower.includes('video') ||
    lower.includes('youtube.com') ||
    lower.includes('youtu.be') ||
    lower.includes('vimeo.com');
};

// Convert YouTube/Vimeo URLs to embeddable format
const getEmbedUrl = (url: string): string | null => {
  // YouTube: youtube.com/watch?v=ID or youtu.be/ID
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo: vimeo.com/ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const CATEGORY_CONFIG: Record<string, { icon: string; label: string; labelDe: string; color: string; bg: string }> = {
  announcement: { icon: '', label: 'Announcement', labelDe: 'Ankündigung', color: '#D6B160', bg: '#fef9e7' },
  webinar:      { icon: '', label: 'Webinar', labelDe: 'Webinar', color: '#4f46e5', bg: '#e0e7ff' },
  event:        { icon: '', label: 'Event', labelDe: 'Veranstaltung', color: '#9333ea', bg: '#f3e8ff' },
  news:         { icon: '', label: 'News', labelDe: 'Nachrichten', color: '#2563eb', bg: '#dbeafe' },
  update:       { icon: '', label: 'Update', labelDe: 'Aktualisierung', color: '#4f46e5', bg: '#e0e7ff' },
  tip:          { icon: '', label: 'Investment Tip', labelDe: 'Investment-Tipp', color: '#059669', bg: '#d1fae5' },
  journey:      { icon: '', label: 'Journey', labelDe: 'Reise', color: '#059669', bg: '#d1fae5' },
  construction: { icon: '', label: 'Construction', labelDe: 'Baufortschritt', color: '#d97706', bg: '#fef3c7' },
};

// ==================== STYLES ====================
const styles: Record<string, React.CSSProperties> = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#f0f2f5',
    minHeight: '100vh',
    paddingBottom: 20,
  },
  header: {
    background: '#1a1a1a',
    padding: '16px 20px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerTitle: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  headerGold: {
    color: '#D6B160',
  },
  feedContainer: {
    padding: '12px 16px',
    maxWidth: 600,
    margin: '0 auto',
  },
  // Post Card - Facebook Style
  postCard: {
    background: '#fff',
    borderRadius: 12,
    marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  postPinnedBanner: {
    background: 'linear-gradient(90deg, #D6B160, #e8c96d)',
    padding: '6px 16px',
    fontSize: 12,
    fontWeight: 600,
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  postHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 16px 8px',
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #D6B160, #b8943d)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    flexShrink: 0,
  },
  postAuthorName: {
    fontWeight: 600,
    fontSize: 14,
    color: '#1a1a1a',
  },
  postTimestamp: {
    fontSize: 12,
    color: '#8e8e8e',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  categoryBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 600,
  },
  postBody: {
    padding: '0 16px 12px',
  },
  postTitle: {
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 700,
    fontSize: 16,
    color: '#1a1a1a',
    marginBottom: 6,
    lineHeight: 1.3,
  },
  postContent: {
    fontSize: 14,
    color: '#333',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
  },
  readMore: {
    color: '#D6B160',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    marginTop: 4,
  },
  // Image Carousel
  imageContainer: {
    position: 'relative' as const,
    width: '100%',
    overflow: 'hidden',
  },
  imageSlider: {
    display: 'flex',
    transition: 'transform 0.3s ease',
  },
  postImage: {
    width: '100%',
    flexShrink: 0,
    objectFit: 'cover' as const,
    maxHeight: 350,
    background: '#f0f0f0',
  },
  imageDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 0',
  },
  imageDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
  },
  // Action Button (CTA)
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    margin: '0 16px 12px',
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #D6B160, #c9a24e)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
  },
  // Engagement Bar
  engagementBar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 16px 8px',
    fontSize: 13,
    color: '#8e8e8e',
  },
  // Action Buttons (Like / Comment)
  actionsRow: {
    display: 'flex',
    borderTop: '1px solid #f0f0f0',
    padding: '0 8px',
  },
  actionBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '10px 0',
    background: 'none',
    border: 'none',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    color: '#65676b',
    transition: 'background 0.15s',
  },
  actionBtnLiked: {
    color: '#D6B160',
    fontWeight: 600,
  },
  // Comments Section
  commentsSection: {
    borderTop: '1px solid #f0f0f0',
    padding: '10px 16px',
  },
  viewAllComments: {
    color: '#8e8e8e',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: '0 0 8px',
  },
  commentItem: {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
  },
  commentAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#e4e6eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 600,
    color: '#65676b',
    flexShrink: 0,
  },
  commentBubble: {
    background: '#f0f2f5',
    borderRadius: 16,
    padding: '8px 12px',
    flex: 1,
  },
  commentName: {
    fontWeight: 600,
    fontSize: 12,
    color: '#1a1a1a',
  },
  commentText: {
    fontSize: 13,
    color: '#333',
    lineHeight: 1.4,
    marginTop: 2,
  },
  commentTime: {
    fontSize: 11,
    color: '#8e8e8e',
    marginTop: 4,
    paddingLeft: 12,
  },
  // Comment Input
  commentInputRow: {
    display: 'flex',
    gap: 8,
    padding: '8px 0 0',
    alignItems: 'flex-end',
  },
  commentInputAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #D6B160, #b8943d)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    flexShrink: 0,
  },
  commentInputWrapper: {
    flex: 1,
    background: '#f0f2f5',
    borderRadius: 20,
    padding: '8px 14px',
    display: 'flex',
    alignItems: 'center',
  },
  commentInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    fontSize: 14,
    outline: 'none',
    resize: 'none' as const,
    maxHeight: 80,
    lineHeight: 1.3,
    fontFamily: 'inherit',
  },
  sendBtn: {
    background: 'none',
    border: 'none',
    color: '#D6B160',
    fontWeight: 700,
    fontSize: 14,
    cursor: 'pointer',
    padding: '0 0 0 8px',
  },
  sendBtnDisabled: {
    color: '#ccc',
    cursor: 'default',
  },
  // Pull to refresh
  pullIndicator: {
    textAlign: 'center' as const,
    padding: '12px 0',
    fontSize: 13,
    color: '#8e8e8e',
  },
  // Loading & Empty
  loadingState: {
    textAlign: 'center' as const,
    padding: 60,
    color: '#8e8e8e',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: 60,
    color: '#8e8e8e',
  },
  commentsDisabled: {
    textAlign: 'center' as const,
    fontSize: 13,
    color: '#8e8e8e',
    padding: '8px 0',
    fontStyle: 'italic',
  },
};


// ==================== STORY STYLES ====================
const storyStyles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px 0 8px',
    maxWidth: 600,
    margin: '0 auto',
  },
  scrollRow: {
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    padding: '0 16px 8px',
    scrollbarWidth: 'none',
    WebkitOverflowScrolling: 'touch',
  },
  storyItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    cursor: 'pointer',
    flexShrink: 0,
    width: 76,
  },
  storyRing: {
    width: 68,
    height: 68,
    borderRadius: '50%',
    padding: 3,
    background: 'linear-gradient(135deg, #D6B160, #e8c96d, #c49b3d)',
  },
  storyImageWrap: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '2px solid #fff',
  },
  storyImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  storyLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: '#333',
    textAlign: 'center',
    lineHeight: '1.2',
    maxWidth: 76,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Fullscreen story viewer
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.95)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
  },
  viewerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)',
  },
  viewerClose: {
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: 28,
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
  },
  viewerCategory: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    color: '#D6B160',
    background: 'rgba(214,177,96,0.15)',
  },
  viewerImageContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  viewerImage: {
    width: '100%',
    maxHeight: '60vh',
    objectFit: 'contain',
  },
  viewerInfo: {
    padding: '20px 20px 40px',
    background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  viewerTitle: {
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 20,
    fontWeight: 700,
    color: '#fff',
    marginBottom: 8,
  },
  viewerDesc: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    lineHeight: 1.5,
    marginBottom: 12,
  },
  viewerMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
  },
  viewerTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: 'rgba(255,255,255,0.15)',
  },
  viewerCTA: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #D6B160, #c49b3d)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 15,
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
    width: '100%',
  },
  viewerNav: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: '#fff',
    fontSize: 24,
    width: 44,
    height: 44,
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  progressBar: {
    display: 'flex',
    gap: 4,
    padding: '8px 16px 0',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 11,
  },
  progressSegment: {
    flex: 1,
    height: 3,
    borderRadius: 2,
    overflow: 'hidden',
  },
};

// ==================== CATEGORY CONFIG FOR STORIES ====================
const STORY_CATEGORY_CONFIG: Record<string, { icon: string; label: string }> = {
  listing: { icon: '🏠', label: 'New Listing' },
  unit: { icon: '🏢', label: 'Available Unit' },
  offer: { icon: '🔥', label: 'Special Offer' },
  update: { icon: '📢', label: 'Update' },
  promo: { icon: '⭐', label: 'Featured' },
};

// ==================== STORY VIEWER COMPONENT ====================
const StoryViewer: React.FC<{
  stories: Story[];
  initialIndex: number;
  isDE: boolean;
  onClose: () => void;
}> = ({ stories, initialIndex, isDE, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const story = stories[currentIndex];

  const STORY_DURATION = 6000; // 6 seconds per story

  const goNext = useCallback(() => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(0);
    } else {
      onClose();
    }
  }, [currentIndex, stories.length, onClose]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setProgress(0);
    }
  }, [currentIndex]);

  useEffect(() => {
    setProgress(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          goNext();
          return 0;
        }
        return prev + (100 / (STORY_DURATION / 50));
      });
    }, 50);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [currentIndex, goNext]);

  const title = isDE && story.titleDe ? story.titleDe : story.title;
  const desc = isDE && story.descriptionDe ? story.descriptionDe : story.description;
  const cat = STORY_CATEGORY_CONFIG[story.category] || STORY_CATEGORY_CONFIG.listing;

  return (
    <div style={storyStyles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Progress bars */}
      <div style={storyStyles.progressBar}>
        {stories.map((_, i) => (
          <div key={i} style={{ ...storyStyles.progressSegment, background: 'rgba(255,255,255,0.3)' }}>
            <div style={{
              height: '100%',
              borderRadius: 2,
              background: '#D6B160',
              width: i < currentIndex ? '100%' : i === currentIndex ? `${progress}%` : '0%',
              transition: i === currentIndex ? 'none' : 'width 0.3s',
            }} />
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={storyStyles.viewerHeader}>
        <span style={storyStyles.viewerCategory}>{cat.icon} {cat.label}</span>
        <button style={storyStyles.viewerClose} onClick={onClose}>&times;</button>
      </div>

      {/* Image */}
      <div style={storyStyles.viewerImageContainer}>
        <img
          src={fixDriveImageUrl(story.imageUrl)}
          alt={title}
          style={storyStyles.viewerImage}
          onError={(e) => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect fill="%231a1a1a" width="400" height="400"/><text fill="%23D6B160" font-size="40" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">🏠</text></svg>'; }}
        />
      </div>

      {/* Info overlay at bottom */}
      <div style={storyStyles.viewerInfo}>
        <div style={storyStyles.viewerTitle}>{title}</div>
        {desc && <div style={storyStyles.viewerDesc}>{desc}</div>}
        <div style={storyStyles.viewerMeta}>
          {story.project && <span style={storyStyles.viewerTag}>{story.project}</span>}
          {story.price && <span style={storyStyles.viewerTag}>{story.price}</span>}
          {story.bedrooms && <span style={storyStyles.viewerTag}>{story.bedrooms}</span>}
          {story.location && <span style={storyStyles.viewerTag}>{story.location}</span>}
        </div>
        {story.linkLabel && story.linkUrl && (
          <button
            style={storyStyles.viewerCTA}
            onClick={() => {
              if (story.linkUrl!.startsWith('http')) window.open(story.linkUrl!, '_blank');
            }}
          >
            {story.linkLabel} →
          </button>
        )}
      </div>

      {/* Nav arrows */}
      {currentIndex > 0 && (
        <button
          style={{ ...storyStyles.viewerNav, left: 8 }}
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
        >
          ‹
        </button>
      )}
      {currentIndex < stories.length - 1 && (
        <button
          style={{ ...storyStyles.viewerNav, right: 8 }}
          onClick={(e) => { e.stopPropagation(); goNext(); }}
        >
          ›
        </button>
      )}
    </div>
  );
};

// ==================== STORIES CAROUSEL COMPONENT ====================
const StoriesCarousel: React.FC<{
  stories: Story[];
  isDE: boolean;
}> = ({ stories, isDE }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  if (stories.length === 0) return null;

  const openStory = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  return (
    <>
      <div style={storyStyles.container}>
        <div style={storyStyles.scrollRow}>
          {stories.map((story, i) => {
            const label = isDE && story.titleDe ? story.titleDe : story.title;
            return (
              <div key={story.id} style={storyStyles.storyItem} onClick={() => openStory(i)}>
                <div style={storyStyles.storyRing}>
                  <div style={storyStyles.storyImageWrap}>
                    <img
                      src={fixDriveImageUrl(story.imageUrl)}
                      alt={label}
                      style={storyStyles.storyImage as React.CSSProperties}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="68" height="68"><rect fill="%231a1a1a" width="68" height="68"/><text fill="%23D6B160" font-size="24" x="50%" y="50%" text-anchor="middle" dominant-baseline="middle">🏠</text></svg>';
                      }}
                    />
                  </div>
                </div>
                <span style={storyStyles.storyLabel as React.CSSProperties}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {viewerOpen && (
        <StoryViewer
          stories={stories}
          initialIndex={viewerIndex}
          isDE={isDE}
          onClose={() => setViewerOpen(false)}
        />
      )}

      <style>{`
        div[style*="scrollbar-width"]::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
};

// ==================== POST CARD COMPONENT ====================
const PostCard: React.FC<{
  post: NewsfeedPost;
  investorId: string;
  investorName: string;
  isDE: boolean;
  apiUrl: string;
}> = ({ post, investorId, investorName, isDE, apiUrl }) => {
  const [isLiked, setIsLiked] = useState(post.likes?.includes(investorId) || false);
  const [likeCount, setLikeCount] = useState(post.likes?.length || 0);
  const [comments, setComments] = useState<Comment[]>(post.comments || []);
  const [showAllComments, setShowAllComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const touchStartRef = useRef<number | null>(null);

  const title = isDE && post.titleDe ? post.titleDe : post.title;
  const content = isDE && post.contentDe ? post.contentDe : post.content;
  const isLong = content.length > 200;
  const displayContent = isLong && !expanded ? content.slice(0, 200) + '...' : content;
  const cat = CATEGORY_CONFIG[post.type] || CATEGORY_CONFIG.announcement;

  // Like toggle
  const handleLike = async () => {
    if (isLiking) return;
    setIsLiking(true);
    const newLiked = !isLiked;
    setIsLiked(newLiked);
    setLikeCount(prev => newLiked ? prev + 1 : prev - 1);
    try {
      await fetch(`${apiUrl}/api/newsfeed/${post.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: investorId }),
      });
    } catch {
      setIsLiked(!newLiked);
      setLikeCount(prev => newLiked ? prev - 1 : prev + 1);
    } finally {
      setIsLiking(false);
    }
  };

  // Submit comment
  const handleSubmitComment = async () => {
    const text = commentText.trim();
    if (!text || isSubmitting) return;
    setIsSubmitting(true);

    const optimisticComment: Comment = {
      id: 'temp-' + Date.now(),
      userId: investorId,
      userName: investorName,
      text,
      createdAt: new Date().toISOString(),
    };
    setComments(prev => [...prev, optimisticComment]);
    setCommentText('');

    try {
      const res = await fetch(`${apiUrl}/api/newsfeed/${post.id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: investorId, userName: investorName, text }),
      });
      const data = await res.json();
      if (data.comment) {
        setComments(prev => prev.map(c => c.id === optimisticComment.id ? data.comment : c));
      }
    } catch {
      setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  // Auto-resize textarea
  const handleCommentInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  // Visible comments
  const visibleComments = showAllComments ? comments : comments.slice(-3);
  const hasMoreComments = comments.length > 3 && !showAllComments;

  // Open action URL
  const handleAction = () => {
    if (post.actionUrl) {
      if (post.actionUrl.startsWith('http')) {
        window.open(post.actionUrl, '_blank');
      }
    }
  };

  const handleAction2 = () => {
    if (post.actionUrl2) {
      if (post.actionUrl2.startsWith('http')) {
        window.open(post.actionUrl2, '_blank');
      }
    }
  };

  return (
    <div style={styles.postCard}>
      {/* Pinned Banner */}
      {post.pinned && (
        <div style={styles.postPinnedBanner}>
          📌 {isDE ? 'Angepinnter Beitrag' : 'Pinned Post'}
        </div>
      )}

      {/* Header: Avatar + Author + Time + Badge */}
      <div style={styles.postHeader}>
        <div style={styles.avatar}>{getInitials(post.author || 'IM')}</div>
        <div style={{ flex: 1 }}>
          <div style={styles.postAuthorName}>{post.author || 'Asset Desk'}</div>
          <div style={styles.postTimestamp}>
            {post.createdAt && getTimeAgo(post.createdAt)}
            <span style={{ margin: '0 4px' }}>·</span>
            <span style={{
              ...styles.categoryBadge,
              color: cat.color,
              background: cat.bg,
            }}>
              {cat.icon} {isDE ? cat.labelDe : cat.label}
            </span>
          </div>
        </div>
      </div>

      {/* Title + Content */}
      <div style={styles.postBody}>
        <div style={styles.postTitle}>{title}</div>
        <div style={styles.postContent}>{displayContent}</div>
        {isLong && !expanded && (
          <button style={styles.readMore} onClick={() => setExpanded(true)}>
            {isDE ? 'Mehr anzeigen' : 'See more'}
          </button>
        )}
      </div>

      {/* Video */}
      {post.videoUrl && (() => {
        const embedUrl = getEmbedUrl(post.videoUrl);
        if (embedUrl) {
          return (
            <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: '#000' }}>
              <iframe
                src={embedUrl}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={title}
              />
            </div>
          );
        }
        return (
          <video
            controls
            playsInline
            preload="metadata"
            style={{ width: '100%', maxHeight: 400, background: '#000' }}
          >
            <source src={post.videoUrl} />
          </video>
        );
      })()}

      {/* Image Carousel */}
      {post.imageUrls && post.imageUrls.length > 0 && (
        <div
          style={styles.imageContainer}
          onTouchStart={(e) => { touchStartRef.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchStartRef.current === null) return;
            const diff = touchStartRef.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
              if (diff > 0 && currentImage < post.imageUrls.length - 1) setCurrentImage(currentImage + 1);
              if (diff < 0 && currentImage > 0) setCurrentImage(currentImage - 1);
            }
            touchStartRef.current = null;
          }}
        >
          <div style={{
            ...styles.imageSlider,
            transform: `translateX(-${currentImage * 100}%)`,
          }}>
            {post.imageUrls.map((url, i) => (
              <img key={i} src={fixDriveImageUrl(url)} alt="" style={styles.postImage}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ))}
          </div>
          {post.imageUrls.length > 1 && (
            <>
              {/* Left/Right arrows */}
              {currentImage > 0 && (
                <button
                  onClick={() => setCurrentImage(currentImage - 1)}
                  style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >‹</button>
              )}
              {currentImage < post.imageUrls.length - 1 && (
                <button
                  onClick={() => setCurrentImage(currentImage + 1)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >›</button>
              )}
              {/* Counter badge */}
              <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 500 }}>
                {currentImage + 1}/{post.imageUrls.length}
              </div>
              <div style={styles.imageDots}>
                {post.imageUrls.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImage(i)}
                    style={{
                      ...styles.imageDot,
                      background: i === currentImage ? '#D6B160' : '#ddd',
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Action Buttons (CTAs) */}
      {(post.actionLabel && post.actionUrl) || (post.actionLabel2 && post.actionUrl2) ? (
        <div style={{ padding: '0 16px 12px', display: 'flex', gap: 8, flexWrap: 'wrap' as const }}>
          {post.actionLabel && post.actionUrl && (
            <button style={{ ...styles.actionButton, margin: 0, flex: 1 }} onClick={handleAction}>
              {post.actionLabel} →
            </button>
          )}
          {post.actionLabel2 && post.actionUrl2 && (
            <button style={{ ...styles.actionButton, margin: 0, flex: 1, background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)' }} onClick={handleAction2}>
              {post.actionLabel2} →
            </button>
          )}
        </div>
      ) : null}

      {/* Engagement Bar */}
      <div style={styles.engagementBar}>
        <span>
          {likeCount > 0 && (
            <>❤️ {likeCount} {likeCount === 1 ? (isDE ? 'Gefällt mir' : 'Like') : (isDE ? 'Gefällt mir' : 'Likes')}</>
          )}
        </span>
        <span>
          {comments.length > 0 && (
            <>{comments.length} {comments.length === 1 ? (isDE ? 'Kommentar' : 'Comment') : (isDE ? 'Kommentare' : 'Comments')}</>
          )}
        </span>
      </div>

      {/* Like / Comment Buttons */}
      <div style={styles.actionsRow}>
        <button
          style={{ ...styles.actionBtn, ...(isLiked ? styles.actionBtnLiked : {}) }}
          onClick={handleLike}
        >
          {isLiked ? '❤️' : '🤍'} {isDE ? 'Gefällt mir' : 'Like'}
        </button>
        <button
          style={styles.actionBtn}
          onClick={() => textareaRef.current?.focus()}
        >
          💬 {isDE ? 'Kommentieren' : 'Comment'}
        </button>
      </div>

      {/* Comments Section */}
      {(comments.length > 0 || post.commentsEnabled) && (
        <div style={styles.commentsSection}>
          {/* View all comments */}
          {hasMoreComments && (
            <button
              style={styles.viewAllComments}
              onClick={() => setShowAllComments(true)}
            >
              {isDE
                ? `Alle ${comments.length} Kommentare anzeigen`
                : `View all ${comments.length} comments`}
            </button>
          )}

          {/* Comment List */}
          {visibleComments.map((comment) => (
            <div key={comment.id} style={styles.commentItem}>
              <div style={styles.commentAvatar}>
                {getInitials(comment.userName || 'U')}
              </div>
              <div>
                <div style={styles.commentBubble}>
                  <div style={styles.commentName}>{comment.userName || 'User'}</div>
                  <div style={styles.commentText}>{comment.text}</div>
                </div>
                <div style={styles.commentTime}>
                  {comment.createdAt && getTimeAgo(comment.createdAt)}
                </div>
              </div>
            </div>
          ))}

          {/* Comment Input */}
          {post.commentsEnabled ? (
            <div style={styles.commentInputRow}>
              <div style={styles.commentInputAvatar}>
                {getInitials(investorName)}
              </div>
              <div style={styles.commentInputWrapper}>
                <textarea
                  ref={textareaRef}
                  rows={1}
                  style={styles.commentInput}
                  placeholder={isDE ? 'Kommentar schreiben...' : 'Write a comment...'}
                  value={commentText}
                  onChange={handleCommentInput}
                  onKeyDown={handleKeyDown}
                />
                <button
                  style={{
                    ...styles.sendBtn,
                    ...(commentText.trim() ? {} : styles.sendBtnDisabled),
                  }}
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() || isSubmitting}
                >
                  {isSubmitting ? '...' : (isDE ? 'Senden' : 'Send')}
                </button>
              </div>
            </div>
          ) : (
            <div style={styles.commentsDisabled}>
              {isDE ? 'Kommentare sind deaktiviert' : 'Comments are disabled'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// ==================== MAIN NEWSFEED VIEW ====================
const NewsfeedView: React.FC<NewsfeedViewProps> = ({
  investorId,
  investorName,
  isDE,
  apiUrl,
  onNavigate,
  demoMode,
}) => {
  const [posts, setPosts] = useState<NewsfeedPost[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const fetchStories = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/stories`);
      if (res.ok) {
        const data = await res.json();
        setStories(data.stories || []);
      }
    } catch {
      // Stories are non-critical, silently fail
    }
  }, [apiUrl]);

  const fetchPosts = useCallback(async () => {
    if (demoMode) {
      // Sample feed, pinned post first — matches the ordering below.
      setPosts(DEMO_FEED_POSTS as NewsfeedPost[]);
      setError(null);
      setLoading(false);
      setRefreshing(false);
      return;
    }
    try {
      const res = await fetch(`${apiUrl}/api/newsfeed`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');

      let allPosts: NewsfeedPost[] = data.posts || [];
      // Sort: pinned first, then newest
      allPosts.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        const aTime = typeof a.createdAt === 'object' ? a.createdAt._seconds : new Date(a.createdAt).getTime() / 1000;
        const bTime = typeof b.createdAt === 'object' ? b.createdAt._seconds : new Date(b.createdAt).getTime() / 1000;
        return bTime - aTime;
      });
      setPosts(allPosts);
      setError(null);
    } catch (err: any) {
      setError(appLanguage() === 'de' ? 'Neuigkeiten konnten nicht geladen werden' : (err.message || 'Failed to load newsfeed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [apiUrl, demoMode]);

  useEffect(() => {
    fetchPosts();
    fetchStories();
  }, [fetchPosts, fetchStories]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPosts();
    fetchStories();
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          📰 <span style={styles.headerGold}>Asset Desk</span>&nbsp;{isDE ? 'Neuigkeiten' : 'Newsfeed'}
        </div>
        <button
          onClick={handleRefresh}
          style={{
            background: 'none',
            border: '1px solid rgba(214,177,96,0.4)',
            color: '#D6B160',
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {refreshing ? '...' : '↻'}
        </button>
      </div>

      {/* Stories Carousel */}
      <StoriesCarousel stories={stories} isDE={isDE} />

      {/* Category Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: 8,
        padding: '12px 16px',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        maxWidth: 600,
        margin: '0 auto',
      }}>
        <style>{`.filter-tabs::-webkit-scrollbar { display: none; }`}</style>
        <button
          onClick={() => setActiveFilter('all')}
          style={{
            flexShrink: 0,
            padding: '8px 16px',
            borderRadius: 20,
            border: 'none',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            background: activeFilter === 'all' ? '#1a1a1a' : '#fff',
            color: activeFilter === 'all' ? '#D6B160' : '#666',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          {isDE ? 'Alle' : 'All'}
        </button>
        {Object.entries(CATEGORY_CONFIG).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setActiveFilter(key)}
            style={{
              flexShrink: 0,
              padding: '8px 16px',
              borderRadius: 20,
              border: 'none',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              background: activeFilter === key ? cat.color : '#fff',
              color: activeFilter === key ? '#fff' : '#666',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            {cat.icon} {isDE ? cat.labelDe : cat.label}
          </button>
        ))}
      </div>

      {/* Pull to refresh indicator */}
      {refreshing && (
        <div style={styles.pullIndicator}>
          {isDE ? 'Aktualisieren...' : 'Refreshing...'}
        </div>
      )}

      {/* Feed */}
      <div style={styles.feedContainer}>
        {loading && (
          <div style={styles.loadingState}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📰</div>
            <div>{isDE ? 'Lade Neuigkeiten...' : 'Loading newsfeed...'}</div>
          </div>
        )}

        {error && !loading && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <div>{error}</div>
            <button
              onClick={handleRefresh}
              style={{ ...styles.readMore, marginTop: 12 }}
            >
              {isDE ? 'Erneut versuchen' : 'Try again'}
            </button>
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {isDE ? 'Noch keine Beiträge' : 'No posts yet'}
            </div>
            <div style={{ fontSize: 13 }}>
              {isDE ? 'Schau bald wieder vorbei!' : 'Check back soon for updates!'}
            </div>
          </div>
        )}

        {posts
          .filter(post => activeFilter === 'all' || post.type === activeFilter)
          .map(post => (
          <PostCard
            key={post.id}
            post={post}
            investorId={investorId}
            investorName={investorName}
            isDE={isDE}
            apiUrl={apiUrl}
          />
        ))}

        {!loading && !error && activeFilter !== 'all' && posts.filter(p => p.type === activeFilter).length === 0 && posts.length > 0 && (
          <div style={styles.emptyState}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {isDE ? 'Keine Beiträge in dieser Kategorie' : 'No posts in this category'}
            </div>
            <button
              onClick={() => setActiveFilter('all')}
              style={{ ...styles.readMore, marginTop: 12 }}
            >
              {isDE ? 'Alle anzeigen' : 'Show all'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsfeedView;